/* eslint-disable react/jsx-no-literals */
import { Grid, SxProps, Typography, styled } from '@mui/material';
import React, { FC, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Linkable } from '@/components/blocks/Linkable';
import { useIsMobile } from '@/data/hooks/useIsMobile';
import { combineSX } from '@/utils/combineSX';
import {
	DpPopulateBlockDesktopFrameSX,
	DpPopulateBlockMobileContentSX,
	DpPopulateBlockMobileFrameSX,
	DpPopulateBlockMobileHeaderSX,
	DpPopulateCategorySX,
	DpPopulateLinkSX,
	DpPopulateUlSX,
} from '@/components/blocks/DpLibrary/DpPopulate/style';

export const DpPopulateCategory: React.FC<{ children: React.ReactNode; sx?: SxProps }> = ({
	children,
	sx,
	...props
}) => (
	<Typography
		component="h3"
		sx={combineSX([DpPopulateCategorySX, ...(Array.isArray(sx) ? sx : [sx])])}
		{...props}
	>
		{children}
	</Typography>
);

export const DpPopulateLink: React.FC<{ children: JSX.Element; href?: string; sx?: SxProps }> = ({
	children,
	href,
	sx,
	...props
}) => (
	<li>
		<Linkable
			href={href || '#'}
			sx={combineSX([DpPopulateLinkSX, ...(Array.isArray(sx) ? sx : [sx])])}
			{...props}
		>
			{children}
		</Linkable>
	</li>
);

const DpPopulateUl = styled('ul')({});

export const DpPopulateBlockDesktop: React.FC<{
	title: string;
	framesx?: SxProps;
	titlesx?: SxProps;
	listsx?: SxProps;
	children: React.ReactNode;
}> = ({ children, title, framesx, titlesx, listsx }) => (
	<Grid
		item
		xs={5}
		md={1}
		sx={combineSX([
			DpPopulateBlockDesktopFrameSX,
			...(Array.isArray(framesx) ? framesx : [framesx]),
		])}
	>
		<DpPopulateCategory sx={titlesx}>{title}</DpPopulateCategory>
		<DpPopulateUl sx={combineSX([DpPopulateUlSX, ...(Array.isArray(listsx) ? listsx : [listsx])])}>
			{children}
		</DpPopulateUl>
	</Grid>
);

export const DpPopulateBlockMobile: FC<{
	title: string;
	framesx?: SxProps;
	titlesx?: SxProps;
	listsx?: SxProps;
	children: React.ReactNode;
}> = ({ children, title, framesx, titlesx, listsx }) => {
	const [isExpanded, setIsExpanded] = useState(false);

	const toggleExpand = () => {
		setIsExpanded(!isExpanded);
	};

	return (
		<Grid
			xs={12}
			sx={combineSX([
				DpPopulateBlockMobileFrameSX,
				...(Array.isArray(framesx) ? framesx : [framesx]),
			])}
		>
			<Grid
				sx={combineSX([
					DpPopulateBlockMobileHeaderSX(isExpanded),
					...(Array.isArray(titlesx) ? titlesx : [titlesx]),
				])}
				onClick={toggleExpand}
			>
				<Grid>{title}</Grid> {!isExpanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
			</Grid>
			{isExpanded ? (
				<Grid sx={DpPopulateBlockMobileContentSX}>
					<DpPopulateUl
						sx={combineSX([DpPopulateUlSX, ...(Array.isArray(listsx) ? listsx : [listsx])])}
					>
						{children}
					</DpPopulateUl>
				</Grid>
			) : null}
		</Grid>
	);
};

export const DpPopulateBlock: React.FC<{
	children: React.ReactNode;
	title: string;
	desktoptitlesx?: SxProps;
	desktoplistsx?: SxProps;
	desktopframesx?: SxProps;
	mobiletitlesx?: SxProps;
	mobilelistsx?: SxProps;
	mobileframesx?: SxProps;
}> = ({
	children,
	title,
	desktoptitlesx,
	desktopframesx,
	desktoplistsx,
	mobiletitlesx,
	mobileframesx,
	mobilelistsx,
}) => {
	const isMobile = useIsMobile();

	if (isMobile) {
		return (
			<DpPopulateBlockMobile
				title={title}
				titlesx={mobiletitlesx}
				framesx={mobileframesx}
				listsx={mobilelistsx}
			>
				{children}
			</DpPopulateBlockMobile>
		);
	}

	return (
		<DpPopulateBlockDesktop
			title={title}
			titlesx={desktoptitlesx}
			framesx={desktopframesx}
			listsx={desktoplistsx}
		>
			{children}
		</DpPopulateBlockDesktop>
	);
};
